// frontend/src/components/TripRouteSegments.jsx
// Simplified version: Shows planned route (blue) + GPS trail overlay (green)
// See backend/docs/2026-02-05-GPS_BREADCRUMBS_MAP_DISPLAY.md

import React, { useMemo } from 'react';
import { Polyline, useMap } from 'react-leaflet';

const GPS_TRAIL_PANE = 'gpsTrailPane';

// Constants for route styling
const ROUTE_COLOR = '#3B82F6'; // Blue - planned route
const GPS_TRAIL_COLOR = '#22C55E'; // Green - actual traveled path
const POLYLINE_WEIGHT = 3;
const GPS_TRAIL_WEIGHT = 4;
const SOLID_OPACITY = 0.9;
const GPS_TRAIL_OPACITY = 0.9;

/**
 * TripRouteSegments - Renders route polylines with status-aware styling
 *
 * Active trips (blue + green):
 * 1. Planned route (blue) - full route
 * 2. GPS trail (green) - actual traveled path from breadcrumbs
 *
 * Completed trips (green only):
 * - Single solid green line from trip_locations_archive (actual traveled path)
 *
 * @param {Array} routeReferenceLocations - Route coordinates [[lat, lng], ...]
 * @param {Object} tripData - Trip data including status
 * @param {Array} gpsTrail - GPS breadcrumbs [{lat, lng, timestamp}, ...]
 */
const TripRouteSegments = React.memo(({
    routeReferenceLocations,
    tripData,
    gpsTrail,
}) => {
    // Create a custom pane for GPS trail so it renders above other polylines.
    // Uses useMemo (not useEffect) to ensure the pane exists before first render —
    // Polyline components reference it immediately and Leaflet throws if the pane is missing.
    const map = useMap();
    useMemo(() => {
        if (!map.getPane(GPS_TRAIL_PANE)) {
            const pane = map.createPane(GPS_TRAIL_PANE);
            pane.style.zIndex = 610; // Above markerPane (600)
            pane.style.pointerEvents = 'none';
        }
    }, [map]);

    // Validate route data
    const validRouteData = routeReferenceLocations && routeReferenceLocations.length > 1;

    // Convert GPS trail to Leaflet positions format
    const gpsTrailPositions = useMemo(() => {
        if (!gpsTrail || gpsTrail.length < 2) return [];
        return gpsTrail
            .filter(p => p && p.lat != null && p.lng != null)
            .map(p => [Number(p.lat), Number(p.lng)]);
    }, [gpsTrail]);

    const hasGpsTrail = gpsTrailPositions.length > 1;

    // For completed trips, routeReferenceLocations is the actual traveled path
    // (from trip_locations_archive), so render it as green GPS trail color
    const isCompleted = tripData?.status === 'completed';
    const routeColor = isCompleted ? GPS_TRAIL_COLOR : ROUTE_COLOR;
    const routeWeight = isCompleted ? GPS_TRAIL_WEIGHT : POLYLINE_WEIGHT;
    const routeOpacity = isCompleted ? GPS_TRAIL_OPACITY : SOLID_OPACITY;

    // No reference route — still show GPS trail if available
    if (!validRouteData) {
        if (hasGpsTrail) {
            return (
                <Polyline
                    positions={gpsTrailPositions}
                    color={GPS_TRAIL_COLOR}
                    weight={GPS_TRAIL_WEIGHT}
                    opacity={GPS_TRAIL_OPACITY}
                    pane={GPS_TRAIL_PANE}
                    interactive={false}
                />
            );
        }
        return null;
    }

    // Avoid route-index segmentation: nearest-coordinate boundary slicing is
    // unreliable on loop routes where the same stop can appear more than once.
    return (
        <>
            <Polyline
                positions={routeReferenceLocations}
                color={routeColor}
                weight={routeWeight}
                opacity={routeOpacity}
            />

            {/* GPS trail overlay — completed trips already use the archived traveled path. */}
            {!isCompleted && hasGpsTrail && (
                <Polyline
                    positions={gpsTrailPositions}
                    color={GPS_TRAIL_COLOR}
                    weight={GPS_TRAIL_WEIGHT}
                    opacity={GPS_TRAIL_OPACITY}
                    pane={GPS_TRAIL_PANE}
                    interactive={false}
                />
            )}
        </>
    );
}, (prevProps, nextProps) => {
    // Custom memo comparison - only re-render when necessary
    if (prevProps.tripData?.id !== nextProps.tripData?.id) return false;
    if (prevProps.tripData?.status !== nextProps.tripData?.status) return false;
    if (prevProps.routeReferenceLocations !== nextProps.routeReferenceLocations) return false;

    // GPS trail comparison - check length and last point
    const prevTrail = prevProps.gpsTrail || [];
    const nextTrail = nextProps.gpsTrail || [];
    if (prevTrail.length !== nextTrail.length) return false;
    if (prevTrail.length > 0 && nextTrail.length > 0) {
        const prevLast = prevTrail[prevTrail.length - 1];
        const nextLast = nextTrail[nextTrail.length - 1];
        if (prevLast?.lat !== nextLast?.lat || prevLast?.lng !== nextLast?.lng) return false;
    }

    return true; // No re-render needed
});

TripRouteSegments.displayName = 'TripRouteSegments';

export default TripRouteSegments;
